import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Breadcrumb } from "antd";
import { routes } from "../../Routes/Routes";
import { setCartCount } from "../../Features/User/User";
import styles from "./index.module.scss";
import axios from "axios";
import { apiUrl, BASE_URL } from "../../Constant";
import { getOrderVariantTags } from "../../utils/cartVariants";

function ThankYou() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const orderMongoId = location.state?.orderMongoId || searchParams.get("id");
  const displayOrderId = location.state?.orderId || searchParams.get("orderId");
  const userToken = useSelector((state) => state.user?.token);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) =>
    Number(price || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/order/get/singleOrder/${orderMongoId}`,
          { headers: { Authorization: userToken } }
        );
        console.log("Full API response:", JSON.stringify(response.data, null, 2));
        const data = response?.data?.order || response?.data?.data || response?.data;
        if (data) {
          setOrderDetails(data);
          dispatch(setCartCount(0));
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };
    if (orderMongoId && userToken) fetchOrderDetails();
    else if (!orderMongoId) {
      // No mongo ID - still show the page with display orderId
      setLoading(false);
    }
    else setLoading(false);
  }, [orderMongoId, dispatch, userToken]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <p>Loading your order details...</p>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>
          <p>Order not found.</p>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate(routes.homepageUrl)}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const totalAmount = orderDetails?.FinalPrice ||
    ((orderDetails?.OriginalPrice || 0) + (orderDetails?.Shipping_Charge || 0) - (orderDetails?.CouponPrice || 0));

  const address = orderDetails?.Address;

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbBar}>
        <div className={styles.breadcrumbInner}>
          <Breadcrumb className={styles.breadcrumb} items={[
            { title: <Link to="/">Home</Link> },
            { title: "Order Confirmed" },
          ]} />
        </div>
      </div>

      {/* Success Hero */}
      <div className={styles.heroSection}>
        <div className={styles.heroInner}>
          <div className={styles.successCheck}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className={styles.heroTitle}>Order Confirmed</h1>
          <p className={styles.heroSubtitle}>
            Thank you for your order. Your order has been placed successfully and is being processed.
          </p>
          <div className={styles.orderIdBadge}>
            <span className={styles.orderIdLabel}>Order ID</span>
            <span className={styles.orderIdValue}>{orderDetails?.orderId || displayOrderId}</span>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className={styles.progressBar}>
        <div className={styles.progressInner}>
          {["Cart", "Checkout", "Confirmation"].map((step, i) => (
            <React.Fragment key={step}>
              {i > 0 && <div className={`${styles.progressLine} ${styles.progressLineDone}`} />}
              <div className={`${styles.progressStep} ${styles.progressDone} ${i === 2 ? styles.progressActive : ""}`}>
                <div className={styles.progressDot} />
                <span>{step}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.confirmLayout}>

          {/* LEFT: Order Details */}
          <div className={styles.leftColumn}>

            {/* Order Items */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Order Items</h3>
              <div className={styles.orderItemsList}>
                {(orderDetails?.cartData || []).map((item, index) => {
                  const tags = getOrderVariantTags(item);

                  return (
                    <div key={index} className={styles.orderItem}>
                      <div className={styles.orderItemImg}>
                        {item?.productImage && <img src={item.productImage} alt={item?.productName} />}
                      </div>
                      <div className={styles.orderItemInfo}>
                        <div className={styles.orderItemHeader}>
                          <h4>{item?.productName}</h4>
                          <span className={styles.orderItemPrice}>{"\u00A3"}{formatPrice(item?.totalPrice || item?.unitPrice)}</span>
                        </div>
                        {tags.length > 0 && (
                          <div className={styles.orderItemTags}>
                            {tags.map(([label, value], j) => (
                              <span key={j} className={styles.orderItemTag}>
                                <span className={styles.orderItemTagLabel}>{label}:</span> {value}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className={styles.orderItemFooter}>
                          <span className={styles.orderItemQty}>Qty: {item?.quantity}</span>
                          <span className={styles.orderItemUnit}>{"\u00A3"}{formatPrice(item?.unitPrice)} each</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Delivery Address */}
            {address && address?.Name && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Delivery Address</h3>
                <div className={styles.addressBlock}>
                  <div className={styles.addressBlockTop}>
                    {address?.Type && <span className={styles.addressBadge}>{address.Type}</span>}
                    <span className={styles.addressBlockName}>{address?.Name}</span>
                    {address?.Phone_Number && <span className={styles.addressBlockPhone}>{address.Phone_Number}</span>}
                  </div>
                  <p className={styles.addressBlockText}>
                    {[address?.House, address?.Full_Address, address?.City, address?.State]
                      .filter(Boolean)
                      .join(", ")}
                    {address?.Pincode && ` - ${address.Pincode}`}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Summary */}
          <div className={styles.rightColumn}>
            <div className={styles.summaryCard}>
              <h3 className={styles.summaryCardTitle}>Payment Summary</h3>

              <div className={styles.summaryTotals}>
                <div className={styles.summaryRow}>
                  <span>Subtotal</span>
                  <span>{"\u00A3"}{formatPrice(orderDetails?.OriginalPrice)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Shipping</span>
                  <span>
                    {Number(orderDetails?.Shipping_Charge) > 0
                      ? `\u00A3${formatPrice(orderDetails?.Shipping_Charge)}`
                      : "Free"}
                  </span>
                </div>
                {orderDetails?.CouponPrice > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryDiscount}`}>
                    <span>Coupon Discount</span>
                    <span>- {"\u00A3"}{formatPrice(orderDetails?.CouponPrice)}</span>
                  </div>
                )}
                {Number(orderDetails?.VAT_Amount) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>VAT ({orderDetails?.VAT_Rate || 20}%)</span>
                    <span>{"\u00A3"}{formatPrice(orderDetails?.VAT_Amount)}</span>
                  </div>
                )}
                <div className={styles.summaryDivider} />
                <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                  <span>Total Paid{Number(orderDetails?.VAT_Amount) > 0 ? " (inc. VAT)" : ""}</span>
                  <span>{"\u00A3"}{formatPrice(totalAmount)}</span>
                </div>
              </div>

              <div className={styles.paymentStatusRow}>
                <span>Payment Status</span>
                <span className={styles.paymentStatus}>
                  {orderDetails?.PaymentStatus || orderDetails?.payment_status || "Pending"}
                </span>
              </div>

              <div className={styles.paymentStatusRow}>
                <span>Payment Method</span>
                <span>{orderDetails?.PaymentType || orderDetails?.payment_mode || "—"}</span>
              </div>
            </div>

            {/* What's Next */}
            <div className={styles.whatsNext}>
              <h4 className={styles.whatsNextTitle}>What Happens Next?</h4>
              <div className={styles.nextSteps}>
                <div className={styles.nextStep}>
                  <div className={styles.nextStepNum}>1</div>
                  <div>
                    <strong>Order Processing</strong>
                    <p>We're preparing your order for dispatch.</p>
                  </div>
                </div>
                <div className={styles.nextStep}>
                  <div className={styles.nextStepNum}>2</div>
                  <div>
                    <strong>Shipping</strong>
                    <p>You'll receive tracking details via email.</p>
                  </div>
                </div>
                <div className={styles.nextStep}>
                  <div className={styles.nextStepNum}>3</div>
                  <div>
                    <strong>Delivery</strong>
                    <p>Fully insured delivery to your address.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={styles.actionButtons}>
              <button type="button" className={styles.primaryBtn} onClick={() => navigate("/my-account", { state: { tab: "orders" } })}>
                View My Orders
              </button>
              <button type="button" className={styles.secondaryBtn} onClick={() => navigate("/product/all")}>
                Continue Shopping
              </button>
            </div>

            {/* Support */}
            <div className={styles.supportBlock}>
              <p>Need help with your order?</p>
              <a href="mailto:sales@thediamondcounter.com">sales@thediamondcounter.com</a>
              <span className={styles.supportDivider}>|</span>
              <a href="https://wa.me/447793219535" target="_blank" rel="noopener noreferrer">WhatsApp</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThankYou;
